import { NavLink, Route, Routes, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FileText,
  FolderOpen,
  House,
  Mail,
  Pill,
  Settings,
  ScrollText,
  LogOut,
} from 'lucide-react'
import { api, ErreurApi } from './lib/api'
import type { Medecin } from './lib/types'
import { Chargement } from './composants/base'
import Connexion from './pages/Connexion'
import Accueil from './pages/Accueil'
import Patients from './pages/Patients'
import Dossier from './pages/Dossier'
import Documents from './pages/Documents'
import Parametres from './pages/Parametres'
import Journal from './pages/Journal'
import Verification from './pages/Verification'
import APlusTard from './pages/APlusTard'
import BarreRecherche from './composants/BarreRecherche'

export default function App() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['moi'],
    queryFn: () => api<{ medecin: Medecin }>('/api/moi'),
    retry: false,
  })

  if (isLoading) return <Chargement texte="Ouverture de la session…" />

  const nonConnecte = isError && error instanceof ErreurApi && error.statut === 401
  if (nonConnecte || !data) return <Connexion />

  return <Application medecin={data.medecin} />
}

function Application({ medecin }: { medecin: Medecin }) {
  const client = useQueryClient()
  const naviguer = useNavigate()

  const deconnecter = async () => {
    await api('/api/auth/deconnexion', { methode: 'POST' })
    client.clear()
    naviguer('/')
  }

  return (
    <div className="flex min-h-full">
      <nav className="sticky top-0 flex h-screen w-[232px] shrink-0 flex-col gap-1 bg-white px-3 py-5">
        <div className="mb-4 px-2">
          <div className="text-[17px] font-semibold tracking-tight text-bleu-700">doc</div>
          <div className="text-[11px] text-brume">dossiers patients</div>
        </div>

        <Lien vers="/" icone={<House className="h-[18px] w-[18px]" />} libelle="Accueil" />
        <Lien
          vers="/patients"
          icone={<FolderOpen className="h-[18px] w-[18px]" />}
          libelle="Patients"
        />
        <Lien
          vers="/documents"
          icone={<FileText className="h-[18px] w-[18px]" />}
          libelle="Documents"
        />
        <Lien
          vers="/messagerie"
          icone={<Mail className="h-[18px] w-[18px]" />}
          libelle="Messagerie"
          aVenir
        />
        <Lien
          vers="/ordonnances"
          icone={<Pill className="h-[18px] w-[18px]" />}
          libelle="Ordonnances"
          aVenir
        />

        <div className="mt-auto flex flex-col gap-1 pt-4">
          <Lien
            vers="/parametres"
            icone={<Settings className="h-[18px] w-[18px]" />}
            libelle="Paramètres"
          />
          <Lien
            vers="/journal"
            icone={<ScrollText className="h-[18px] w-[18px]" />}
            libelle="Journal des accès"
          />
          <div className="mt-2 flex items-center justify-between gap-2 rounded-lg px-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-encre">
                {medecin.civilite ? `${medecin.civilite} ` : ''}
                {medecin.prenom} {medecin.nom}
              </div>
              <div className="truncate text-[11px] text-brume">{medecin.email}</div>
            </div>
            <button
              className="bouton-discret px-2"
              onClick={deconnecter}
              title="Se déconnecter"
              aria-label="Se déconnecter"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex items-center gap-3 bg-fond/90 px-7 py-4 backdrop-blur">
          <BarreRecherche />
        </header>
        <main className="px-7 pb-16">
          <Routes>
            <Route path="/" element={<Accueil />} />
            <Route path="/patients" element={<Patients />} />
            <Route path="/patients/:id" element={<Dossier />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/verification/:id" element={<Verification />} />
            <Route path="/parametres" element={<Parametres />} />
            <Route path="/journal" element={<Journal />} />
            <Route
              path="/messagerie"
              element={
                <APlusTard
                  titre="Messagerie"
                  phase="phase 5"
                  explication="La boîte dédiée au logiciel, le rattachement des messages et de leurs pièces jointes à un dossier, et la trace des envois. Rien n'est branché pour l'instant : aucune boîte n'est relevée, et l'application ne prétend pas le contraire."
                  manque={[
                    'le fournisseur de messagerie et la boîte dédiée, à créer',
                    'la façon dont les messages arrivent dans l’application',
                  ]}
                />
              }
            />
            <Route
              path="/ordonnances"
              element={
                <APlusTard
                  titre="Ordonnances"
                  phase="phase 4"
                  explication="Modèles de document, blocs de prescription réutilisables, génération en PDF avec l'image de signature, archivage dans le dossier, puis envoi au patient ou à une pharmacie."
                  manque={[
                    'tes modèles d’ordonnance, et celui du Centre Georges François Leclerc',
                    'les champs obligatoires avant de finaliser une ordonnance',
                  ]}
                />
              }
            />
            <Route
              path="*"
              element={
                <div className="py-20 text-center text-[14px] text-brume">
                  Cette page n’existe pas.
                </div>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  )
}

function Lien({
  vers,
  icone,
  libelle,
  aVenir = false,
}: {
  vers: string
  icone: React.ReactNode
  libelle: string
  aVenir?: boolean
}) {
  return (
    <NavLink
      to={vers}
      end={vers === '/'}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[14px] transition-colors ${
          isActive
            ? 'bg-bleu-50 font-medium text-bleu-700'
            : 'text-ardoise hover:bg-slate-50 hover:text-encre'
        }`
      }
    >
      {icone}
      <span className="flex-1">{libelle}</span>
      {/* Une fonction non construite le dit, plutôt que de s'afficher vide. */}
      {aVenir && <span className="text-[10px] text-brume">à venir</span>}
    </NavLink>
  )
}
